import InputField from "components/Common/InputField"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"
import { useMemo, useCallback } from "react"
import { toast } from "react-toastify"
import { Form, FormFeedback, Label, Button, Row, Col, Card, CardBody } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import { useAccountVendors } from "hooks/useAccountVendor"
import {
  useCreateAccountVendorBill,
  useEditAccountVendorBill,
} from "hooks/useAccountVendorBill"

const AccountVendorBillAddEdit = ({ modal, toggleModal, isEdit, editedData }) => {
  const { mutateAsync: createMutation, isPending: createLoading } = useCreateAccountVendorBill()
  const { mutateAsync: editMutation, isPending: editLoading } = useEditAccountVendorBill()

  const { data: vendorResponse, isLoading: vendorLoading } = useAccountVendors(
    { all: true, per_page: 200 },
    modal
  )
  const vendors = vendorResponse?.data?.data || vendorResponse?.data || []

  const vendorOptions = useMemo(
    () => (Array.isArray(vendors) ? vendors.map(item => ({ value: item.id, label: item.name })) : []),
    [vendors]
  )

  const validationSchema = Yup.object({
    vendor_id: Yup.mixed().required("Please select a vendor"),
    bill_no: Yup.string().trim().required("Please enter bill number"),
    bill_date: Yup.string().required("Please select bill date"),
    amount: Yup.number().typeError("Amount must be numeric").integer("Amount must be a whole number").min(1, "Amount must be at least 1").required("Please enter amount"),
    notes: Yup.string().nullable(),
  })

  const onSubmit = useCallback(
    async (values, { resetForm }) => {
      const payload = {
        ...values,
        amount: Number(values.amount),
        bill_no: values.bill_no?.trim(),
        notes: values.notes?.trim() || null,
      }

      const action = isEdit
        ? editMutation({ id: editedData.id, ...payload })
        : createMutation(payload)

      try {
        const res = await action
        toast.success(res?.message ?? `Vendor bill ${isEdit ? "updated" : "created"} successfully.`)
        resetForm()
        toggleModal()
      } catch (error) {
        const msg = error?.response?.data?.message || "Failed to save vendor bill."
        toast.error(msg)
      }
    },
    [isEdit, editedData, editMutation, createMutation, toggleModal]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      vendor_id: editedData?.vendor_id || editedData?.vendor?.id || "",
      bill_no: editedData?.bill_no || "",
      bill_date: editedData?.bill_date || "",
      amount: editedData?.amount ?? "",
      notes: editedData?.notes || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit,
  })

  const loadingAny = createLoading || editLoading || vendorLoading

  return (
    <ThemeModal isOpen={modal} toggle={toggleModal} title={isEdit ? "Edit Vendor Bill" : "Add Vendor Bill"} size="lg">
      <Form onSubmit={e => { e.preventDefault(); formik.handleSubmit() }}>
        <Card className="border">
          <CardBody>
            <h5 className="mb-3">Bill Information</h5>
            <Row>
              <Col md={6}>
                <FormikCreatableSelect name="vendor_id" formik={formik} label="Vendor" options={vendorOptions} loading={vendorLoading} required />
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="bill_no">Bill No</Label>
                  <InputField type="text" name="bill_no" id="bill_no" value={formik.values.bill_no} onChange={formik.handleChange} invalid={formik.touched.bill_no && !!formik.errors.bill_no} />
                  {formik.touched.bill_no && formik.errors.bill_no && <FormFeedback type="invalid">{formik.errors.bill_no}</FormFeedback>}
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="bill_date">Bill Date</Label>
                  <InputField type="date" name="bill_date" id="bill_date" value={formik.values.bill_date} onChange={formik.handleChange} invalid={formik.touched.bill_date && !!formik.errors.bill_date} />
                  {formik.touched.bill_date && formik.errors.bill_date && <FormFeedback type="invalid">{formik.errors.bill_date}</FormFeedback>}
                </div>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Label htmlFor="amount">Amount</Label>
                  <InputField type="number" name="amount" id="amount" min="1" value={formik.values.amount} onChange={formik.handleChange} invalid={formik.touched.amount && !!formik.errors.amount} />
                  {formik.touched.amount && formik.errors.amount && <FormFeedback type="invalid">{formik.errors.amount}</FormFeedback>}
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        <Card className="border mt-3">
          <CardBody>
            <h5 className="mb-3">Additional Details</h5>
            <div className="mb-3">
              <Label htmlFor="notes">Notes</Label>
              <InputField type="textarea" name="notes" id="notes" value={formik.values.notes} onChange={formik.handleChange} invalid={formik.touched.notes && !!formik.errors.notes} />
              {formik.touched.notes && formik.errors.notes && <FormFeedback type="invalid">{formik.errors.notes}</FormFeedback>}
            </div>
          </CardBody>
        </Card>

        <div className="text-end mt-3">
          <Button color="success" type="submit" disabled={loadingAny}>
            {loadingAny ? "Saving..." : isEdit ? "Save Changes" : "Add Vendor Bill"}
          </Button>
        </div>
      </Form>
    </ThemeModal>
  )
}

export default AccountVendorBillAddEdit
